/**
 * Track 组件
 * 职责：声明式埋点组件
 *
 * 双写机制说明：
 * 1. Track 组件点击时直接调用 trackerV2.track() → 上报到 hb_web_test
 * 2. 子元素保留 data-gd-click 属性 → 通过 AutoTrigger 上报到 hb_web
 * 3. 两条线并行，互不影响
 */

import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { trackerV2 } from "../core/TrackerV2";
import type { EnrichOptions } from "../enricher/types";
import { TrackerLogger } from "../utils/logger";
import { trackDataRegistry } from "./TrackDataRegistry";

export interface TrackProps {
  event: string;
  data?: Record<string, any>;
  children: React.ReactNode;
  autoEnrich?: boolean;
  enrichOptions?: EnrichOptions;
  /** 曝光事件名称（如 "button_expose"），如果提供则启用曝光埋点 */
  exposeEvent?: string;
  /** 曝光数据（可选，如果不提供则使用 data） */
  exposeData?: Record<string, any>;
  /** 曝光阈值（0-1），默认 0.6（60%） */
  exposeThreshold?: number;
  /** 曝光时长（ms），默认 1500ms */
  exposeTime?: number;
}

export function Track({
  event,
  data = {},
  children,
  autoEnrich = true,
  enrichOptions,
  exposeEvent,
  exposeData,
  exposeThreshold = 0.6,
  exposeTime = 1500,
}: TrackProps) {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const trackDataRef = useRef<Record<string, any>>(data);
  const exposeDataRef = useRef<Record<string, any>>(exposeData || data);
  const exposeTimerRef = useRef<number | null>(null);
  const hasExposedRef = useRef(false);

  // 生成稳定的 track-id
  const trackId = useMemo(
    () => `track_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    []
  );

  // 更新数据引用
  useEffect(() => {
    trackDataRef.current = data;
  }, [data]);

  // 更新曝光数据引用
  useEffect(() => {
    exposeDataRef.current = exposeData || data;
  }, [exposeData, data]);

  // 注册/注销 + 添加点击事件监听器
  useEffect(() => {
    if (!wrapperRef.current) return;

    const element = wrapperRef.current;

    // 注册到注册表
    trackDataRegistry.register(trackId, {
      event,
      data: trackDataRef,
      autoEnrich,
      enrichOptions,
    });

    // 添加点击事件监听器（使用捕获阶段，确保能捕获到所有子元素的点击）
    const handleElementClick = () => {
      try {
        trackerV2.track(event, trackDataRef.current, {
          targetNode: element,
          enrich: autoEnrich,
          enrichOptions,
        });

        TrackerLogger.track("click", event, trackDataRef.current);
      } catch (error) {
        TrackerLogger.error("Track", `上报失败: ${event}`, error);
      }
    };

    // 使用捕获阶段监听，确保能捕获到子元素的点击
    element.addEventListener("click", handleElementClick, true);

    TrackerLogger.track("register", event, { trackId });

    // 清理函数
    return () => {
      element.removeEventListener("click", handleElementClick, true);
      trackDataRegistry.unregister(trackId);

      TrackerLogger.track("unregister", event, { trackId });
    };
  }, [event, trackId, autoEnrich, enrichOptions]);

  // 曝光埋点监听（使用 IntersectionObserver）
  useEffect(() => {
    if (!exposeEvent || !wrapperRef.current) return;

    const element = wrapperRef.current;
    let observer: IntersectionObserver | null = null;

    // 创建 IntersectionObserver
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 如果已经曝光过，不再处理
          if (hasExposedRef.current) return;

          // 检查是否达到曝光阈值
          if (entry.intersectionRatio < exposeThreshold) {
            // 未达到阈值，清除定时器（元素离开视口，需要重新计时）
            if (exposeTimerRef.current) {
              clearTimeout(exposeTimerRef.current);
              exposeTimerRef.current = null;
            }
            return;
          }

          // 如果已经曝光过，不再处理
          if (hasExposedRef.current) {
            return;
          }

          // 达到阈值，开始计时
          if (!exposeTimerRef.current) {
            exposeTimerRef.current = window.setTimeout(() => {
              try {
                // 上报曝光事件（使用独立的曝光数据）
                trackerV2.track(exposeEvent, exposeDataRef.current, {
                  targetNode: element,
                  enrich: autoEnrich,
                  enrichOptions,
                });

                hasExposedRef.current = true;
                TrackerLogger.track(
                  "expose",
                  exposeEvent,
                  exposeDataRef.current
                );

                // 曝光后断开观察
                if (observer) {
                  observer.unobserve(element);
                }
              } catch (error) {
                TrackerLogger.error(
                  "Track",
                  `曝光上报失败: ${exposeEvent}`,
                  error
                );
              }
            }, exposeTime);
          }
        });
      },
      {
        threshold: exposeThreshold,
      }
    );

    // 开始观察
    observer.observe(element);

    TrackerLogger.track("register", exposeEvent, {
      trackId,
      exposeThreshold,
      exposeTime,
    });

    // 清理函数
    return () => {
      if (exposeTimerRef.current) {
        clearTimeout(exposeTimerRef.current);
        exposeTimerRef.current = null;
      }
      if (observer) {
        observer.disconnect();
      }
      hasExposedRef.current = false;
      TrackerLogger.track("unregister", exposeEvent, { trackId });
    };
    // 移除 exposeData 依赖，因为我们使用 exposeDataRef 来访问最新值
    // 这样可以避免在 exposeData 对象引用变化时重新创建观察者
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    exposeEvent,
    trackId,
    exposeThreshold,
    exposeTime,
    autoEnrich,
    enrichOptions,
  ]);

  // 使用 callback ref 来获取子元素的 DOM 引用
  // 对于曝光埋点，需要实际的 DOM 元素才能被 IntersectionObserver 正确检测
  // 如果 children 是单个元素，直接在其上添加 ref；否则用 div 包裹
  const setWrapperRef = useCallback((node: HTMLElement | null) => {
    wrapperRef.current = node;
  }, []);

  const isSingleElement = React.Children.count(children) === 1;

  if (isSingleElement && React.isValidElement(children)) {
    // 单个元素，直接在其上添加 ref 和属性
    const child = children as React.ReactElement<any>;
    const childRef = (child as any).ref;

    // 合并 ref（使用普通函数，因为 childRef 是动态的，且只在条件内使用）
    const mergedRef = (node: HTMLElement | null) => {
      setWrapperRef(node);
      if (typeof childRef === "function") {
        childRef(node);
      } else if (childRef && typeof childRef === "object") {
        childRef.current = node;
      }
    };

    return React.cloneElement(child, {
      ref: mergedRef,
      "data-track-id": trackId,
    } as any);
  }

  // 多个子元素，用 div 包裹（但不用 display: contents，确保 IntersectionObserver 能检测）
  return (
    <div
      ref={wrapperRef as React.Ref<HTMLDivElement>}
      data-track-id={trackId}
      style={{ display: "contents" }}
    >
      {children}
    </div>
  );
}
